import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Form,
  Popup,
  Grid,
  Image,
  Divider,
  Icon,
  Header,
} from "semantic-ui-react";
import { useFormik } from "formik";
import { useDropzone } from "react-dropzone";
import { image } from "../../../../assets";
import { Student, User } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { initialValues, validationSchema } from "./NewStudentForm.form";
import { buildStorageUrl, ubigeoData, withEmptyOption } from "../../../../utils";
import "./NewStudentForm.scss";

const studentController = new Student();
const userController = new User();

export function NewStudentForm(props) {
  const { academicCycleId } = useParams();
  const {
    onClose,
    onReload,
    gradesOptions,
    paymentCategoriesOptions,
    academicCycle,
    student,
  } = props;

  const { accessToken } = useAuth();
  const [departamentOptions, setDepartamentOptions] = useState([]);
  const [provinceOptions, setProvinceOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [emailAutoMode, setEmailAutoMode] = useState(!student);
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailTaken, setEmailTaken] = useState(false);

  const formik = useFormik({
    initialValues: initialValues(student),
    validationSchema: validationSchema(),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      try {
        if (student) {
          await userController.updateUser(student.userId, formValue);
        } else {
          formValue.academicCycleId = academicCycleId;
          formValue.syllabuId = academicCycle.syllabuId;
          await studentController.createStudent(accessToken, formValue);
        }
        onReload();
        onClose();
      } catch (error) {
        console.error(error);
      }
    },
  });

  const normalizeText = (str) =>
    str
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/\s+/g, ".");

  useEffect(() => {
    if (!emailAutoMode) return;
    const firstname = formik.values.firstname || "";
    const lastname = formik.values.lastname || "";
    if (!firstname && !lastname) return;

    const generated = `${normalizeText(firstname)}.${normalizeText(lastname)}@iepsanisidro.edu.pe`;
    formik.setFieldValue("email", generated);
  }, [formik.values.firstname, formik.values.lastname, emailAutoMode]);

  useEffect(() => {
    const email = formik.values.email;
    if (!email || !!student) return;

    const timer = setTimeout(async () => {
      try {
        setEmailChecking(true);
        const result = await userController.checkEmail(email);
        setEmailTaken(result.exists);
      } catch {
        setEmailTaken(false);
      } finally {
        setEmailChecking(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formik.values.email]);

  useEffect(() => {
    (async () => {
      try {
        if (formik.values.province) {
          const province = formik.values.province;

          const dep = province.split("")[0] + "" + province.split("")[1];
          const pro = province.split("")[2] + "" + province.split("")[3];

          setDistrictOptions([]);
          const districts = ubigeoData.filter(
            (ubigeo) =>
              ubigeo.departamento === dep &&
              ubigeo.provincia === pro &&
              ubigeo.distrito !== "00"
          );
          const options = districts.map((ubigeo) => ({
            value:
              ubigeo.departamento +
              "" +
              ubigeo.provincia +
              "" +
              ubigeo.distrito,
            text: ubigeo.text,
          }));
          setDistrictOptions(options);
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }, [formik.values.province]);

  useEffect(() => {
    (async () => {
      try {
        if (formik.values.departament) {
          const departament = formik.values.departament;

          const dep = departament.split("")[0] + "" + departament.split("")[1];

          setProvinceOptions([]);
          const provinces = ubigeoData.filter(
            (ubigeo) =>
              ubigeo.departamento === dep &&
              ubigeo.provincia !== "00" &&
              ubigeo.distrito === "00"
          );
          const options = provinces.map((ubigeo) => ({
            value:
              ubigeo.departamento +
              "" +
              ubigeo.provincia +
              "" +
              ubigeo.distrito,
            text: ubigeo.text,
          }));
          setProvinceOptions(options);
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }, [formik.values.departament]);

  useEffect(() => {
    (async () => {
      try {
        const departaments = ubigeoData.filter(
          (ubigeo) => ubigeo.provincia === "00" && ubigeo.distrito === "00"
        );
        setDepartamentOptions([]);
        const options = departaments.map((ubigeo) => ({
          value:
            ubigeo.departamento + "" + ubigeo.provincia + "" + ubigeo.provincia,
          text: ubigeo.text,
        }));
        setDepartamentOptions(options);
      } catch (error) {
        console.error(error);
      }
    })();
  }, []);

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    formik.setFieldValue("avatar", URL.createObjectURL(file));
    formik.setFieldValue("fileAvatar", file);
  });

  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/jpeg, image/png",
    onDrop,
  });

  const getAvatar = () => {
    if (formik.values.fileAvatar) {
      return formik.values.avatar;
    } else if (formik.values.avatar) {
      return buildStorageUrl(formik.values.avatar);
    }
    return image.noAvatar;
  };

  const fillFormWithTestData = () => {
    formik.setValues({
      firstname: "Luis Edgardo",
      lastname: "Acuña",
      secondLastname: "Guevara",
      email: "luis.acuna@example.com",
      dni: "76665698",
      phone: "987654321",
      birthDate: "1998-05-22",
      departament: "130000",
      province: "130100",
      district: "130101",
      address: "Urb. Los Alamos Mz. F Lt. 6",
      gradeId: 1,
      paymentCategoryId: 1,
    });
  };

  const parseDate = (fecha) => {
    return fecha ? new Date(fecha).toISOString().split("T")[0] : "";
  };

  return (
    <Form className="student-form" onSubmit={formik.handleSubmit}>
      <Grid columns={2} stackable>
        <Grid.Row className="student-form__data">
          <Grid.Column>
            {typeof student === "object" && student !== null ? (
              <div className="student-form__avatar">
                <div {...getRootProps()} className="student-form__avatar-dropzone">
                  <input {...getInputProps()} />
                  <Image
                    avatar
                    className="student-avatar"
                    centered
                    size="small"
                    src={getAvatar()}
                  />
                  <div className="student-form__avatar-overlay">
                    <Icon name="camera" size="big" />
                    <p>Cambiar foto</p>
                  </div>
                </div>
              </div>
            ) : (
              student
            )}
            <Header as="h4" className="student-form__section-title">Datos personales</Header>
            <Form.Input
              name="firstname"
              label="Nombre"
              placeholder="Ej. Juan Carlos"
              onChange={formik.handleChange}
              value={formik.values.firstname}
              error={formik.errors.firstname}
            />
            <Form.Input
              name="lastname"
              label="Apellido paterno"
              placeholder="Ej. Pérez"
              onChange={formik.handleChange}
              value={formik.values.lastname}
              error={formik.errors.lastname}
            />
            <Form.Input
              name="secondLastname"
              label="Apellido materno"
              placeholder="Ej. García"
              onChange={formik.handleChange}
              value={formik.values.secondLastname}
              error={formik.errors.secondLastname}
            />
            <Form.Input
              name="dni"
              label="DNI"
              placeholder="Ej. 12345678"
              onChange={formik.handleChange}
              value={formik.values.dni}
              error={formik.errors.dni}
            />
            <Form.Select
              className="edunova-select"
              name="gender"
              label="Género"
              placeholder="Selecciona un género"
              clearable
              options={withEmptyOption(
                [
                  { value: "M", text: "Masculino" },
                  { value: "F", text: "Femenino" },
                ],
                "Selecciona un género"
              )}
              onChange={(e, { value }) => formik.setFieldValue("gender", value)}
              value={formik.values.gender}
              error={formik.errors.gender}
            />
            <Popup
              content="Fecha de nacimiento"
              trigger={
                <Form.Input
                  name="birthDate"
                  label="Fecha de nacimiento"
                  type="date"
                  placeholder="Fecha de nacimiento"
                  onChange={formik.handleChange}
                  value={parseDate(formik.values.birthDate)}
                  error={formik.errors.birthDate}
                />
              }
            />
            <Form.Input
              name="email"
              label="Correo electrónico para plataforma"
              placeholder="Ej. juan.perez@iepsanisidro.edu.pe"
              onChange={(e) => {
                setEmailAutoMode(false);
                formik.handleChange(e);
              }}
              value={formik.values.email}
              error={
                emailTaken
                  ? "Este correo ya está en uso, edítalo manualmente"
                  : formik.errors.email
              }
              loading={emailChecking}
              readOnly={!!student}
            />
          </Grid.Column>
          <Grid.Column>
            <Header as="h4" className="student-form__section-title">Ubicación y contacto</Header>
            <Form.Select
              className="edunova-select"
              name="departament"
              label="Departamento"
              placeholder="Selecciona un departamento"
              clearable
              options={withEmptyOption(departamentOptions, "Selecciona un departamento")}
              onChange={(e, { value }) => {
                formik.setFieldValue("departament", value);
                formik.setFieldValue("province", null);
                formik.setFieldValue("district", null);
              }}
              value={formik.values.departament}
              error={formik.errors.departament}
            />
            <Form.Select
              className="edunova-select"
              name="province"
              label="Provincia"
              placeholder="Selecciona una provincia"
              clearable
              options={withEmptyOption(provinceOptions, "Selecciona una provincia")}
              onChange={(e, { value }) =>
                formik.setFieldValue("province", value)
              }
              value={formik.values.province}
              error={formik.errors.province}
            />
            <Form.Select
              className="edunova-select"
              name="district"
              label="Distrito"
              placeholder="Selecciona un distrito"
              clearable
              options={withEmptyOption(districtOptions, "Selecciona un distrito")}
              onChange={(e, { value }) =>
                formik.setFieldValue("district", value)
              }
              value={formik.values.district}
              error={formik.errors.district}
            />
            <Form.Input
              name="address"
              label="Domicilio"
              placeholder="Ej. Av. Los Álamos 123"
              onChange={formik.handleChange}
              value={formik.values.address}
              error={formik.errors.address}
            />
            <Form.Input
              name="phone"
              label="Teléfono"
              placeholder="Ej. 987654321"
              onChange={formik.handleChange}
              value={formik.values.phone}
              error={formik.errors.phone}
            />
            <Form.Input
              name="phone2"
              label="Teléfono adicional"
              placeholder="Ej. 912345678 (opcional)"
              onChange={formik.handleChange}
              value={formik.values.phone2}
              error={formik.errors.phone2}
            />
          </Grid.Column>
        </Grid.Row>

        {!student && (
          <>
            <Divider className="student-form__separator" />
            <Grid.Row>
              <Grid.Column width={16}>
                <Header as="h4" className="student-form__section-title">Matrícula</Header>
              </Grid.Column>
              <Grid.Column width={8}>
                <Form.Select
                  className="edunova-select"
                  name="gradeId"
                  label="Grado"
                  placeholder="Selecciona un grado"
                  clearable
                  options={withEmptyOption(gradesOptions, "Selecciona un grado")}
                  onChange={(e, { value }) => {
                    formik.setFieldValue("gradeId", value);
                  }}
                  value={formik.values.gradeId}
                  error={formik.errors.gradeId}
                />
              </Grid.Column>
              <Grid.Column width={8}>
                <Form.Select
                  className="edunova-select"
                  name="paymentCategoryId"
                  label="Categoría de pago"
                  placeholder="Selecciona una categoría de pago"
                  clearable
                  options={withEmptyOption(paymentCategoriesOptions, "Selecciona una categoría de pago")}
                  onChange={(e, { value }) =>
                    formik.setFieldValue("paymentCategoryId", value)
                  }
                  value={formik.values.paymentCategoryId}
                  error={formik.errors.paymentCategoryId}
                />
              </Grid.Column>
            </Grid.Row>
          </>
        )}

        <Divider className="student-form__separator" />
        <Grid.Row>
          <Grid.Column width={16}>
            <Header as="h4" className="student-form__section-title">Datos del apoderado/tutor</Header>
          </Grid.Column>
          <Grid.Column width={8}>
            <Form.Input
              name="dni_tutor"
              label="DNI del tutor"
              placeholder="Ej. 12345678"
              onChange={formik.handleChange}
              value={formik.values.dni_tutor}
              error={formik.errors.dni_tutor}
            />
          </Grid.Column>
          <Grid.Column width={8}>
            <Form.Select
              className="edunova-select"
              name="familyRelationship"
              label="Parentesco"
              placeholder="Selecciona un parentesco"
              clearable
              options={withEmptyOption(
                [
                  { value: 1, text: "Padre" },
                  { value: 2, text: "Madre" },
                  { value: 3, text: "Hermano(a)" },
                  { value: 4, text: "Tío(a)" },
                  { value: 5, text: "Abuelo(a)" },
                  { value: 6, text: "Padrino(a)" },
                  { value: 7, text: "Otro" },
                ],
                "Selecciona un parentesco"
              )}
              onChange={(e, { value }) =>
                formik.setFieldValue("familyRelationship", value)
              }
              value={formik.values.familyRelationship}
              error={formik.errors.familyRelationship}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={16}>
            <Form.Input
              name="name_tutor"
              label="Nombre del tutor"
              placeholder="Ej. María López García"
              onChange={formik.handleChange}
              value={formik.values.name_tutor}
              error={formik.errors.name_tutor}
            />
          </Grid.Column>
        </Grid.Row>
        {/*  <Grid.Row>
          <Form.Button
            className="student-form__button"
            fluid
            type="button"
            onClick={fillFormWithTestData}
          >
            Fill with Test Data
          </Form.Button>
        </Grid.Row> */}
        <Form.Button
          className={`student-form__button ${student ? "update" : "create"}`}
          type="submit"
          primary
          fluid
          loading={formik.isSubmitting}
          disabled={emailTaken || emailChecking}
        >
          {student ? "Actualizar estudiante" : "Matricular estudiante"}
        </Form.Button>
      </Grid>
    </Form>
  );
}
